import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import apiClient from "../../services/client.js";
import OpportunityFormView from "./OpportunityFormView.js";
import { submitFeedback } from '../../services/feedbackService.js';

// Débogage des variables d'environnement
console.log("API URL:", process.env.REACT_APP_EASY_ASSISTANT_API_URL);
console.log("API Key présente:", process.env.REACT_APP_API_KEY ? "Oui" : "Non");

const OpportunityForm = () => {
  const { userId, opportunityId } = useParams();
  const STAGE = process.env.REACT_APP_STAGE;

  useEffect(() => {
  console.log("STAGE:", STAGE);
  }, [STAGE]);

  const [selectedCommercial, setSelectedCommercial] = useState({ id: null, value: null });
  const [commercialOptions, setCommercialOptions] = useState([]);
  // Change initial state of selectedOpportunity to null
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [opportunityOptions, setOpportunityOptions] = useState([]);

  const [selectedModel, setSelectedModel] = useState('gpt-4o');

  const [storyData, setStoryData] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isWIP, setIsWIP] = useState(false);
  const [isLoadingStory, setIsLoadingStory] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [storyId, setStoryId] = useState(null);
  const [isUrlMode, setIsUrlMode] = useState(false);

  const [maxNbToProcess, setMaxNbToProcess] = useState(30);

  const handleFeedbackSubmit = async (feedbackData) => {
      try {
        await submitFeedback(feedbackData);
        // Optionally add success message or state update
      } catch (error) {
        console.error('Failed to submit feedback:', error);
        // Optionally handle error (show message, etc.)
      }
    };
  
  // ==================================================
  // Automatically fetches the list of commercial 
  // users when component mounts
  // ==================================================
  useEffect(() => {
    console.log("OpportunityForm mounted");
    const fetchCommercials = async () => {
        try {
            const response = await apiClient.get(`/api/v1/users/emails`);
            setCommercialOptions(response.data);
            
            // If userId is provided in URL, find and select the matching commercial
            if (userId) {
              const matchingCommercial = response.data.find(c => c.id === userId);
              if (matchingCommercial) {
                setIsUrlMode(true);
                handleCommercialChange(matchingCommercial);
              }
            }
        } catch (err) {
            console.error("Erreur lors du chargement des commerciaux :", err);
        }
    };
    fetchCommercials();
  }, [userId]);

  // ==================================================
  // Automatically triggers opportunity filtering 
  // when selected commercial changes
  // ==================================================
  useEffect(() => {
    if (selectedCommercial.id) {
      submitFilters(selectedCommercial); // Soumet automatiquement quand les seuils changent
    }
  }, [selectedCommercial]);

  // ==================================================
  // Handle opportunity selection from URL
  // ==================================================
  useEffect(() => {
    if (opportunityId && selectedCommercial?.id) {
      const findAndSelectOpportunity = async () => {
        try {
          const payload = {
            cur_owner: selectedCommercial.id,
          };

          const response = await apiClient.post(`/api/v1/filter`, payload);
          setOpportunityOptions(response.data);

          // Find and select the matching opportunity
          const matchingOpportunity = response.data.find(o => o.id === opportunityId);
          if (matchingOpportunity) {
            const formattedOpportunity = {
              id: matchingOpportunity.id,
              value: matchingOpportunity.name,
              ...matchingOpportunity
            };
            setSelectedOpportunity(formattedOpportunity);
          }
        } catch (error) {
          console.error("Error finding opportunity:", error);
        }
      };

      findAndSelectOpportunity();
    }
  }, [selectedCommercial, opportunityId]);

  // ==================================================
  // Automatically generate timeline when opportunity 
  // is selected in URL mode
  // ==================================================
  useEffect(() => {
    if (isUrlMode && selectedOpportunity?.id) {
      handleGetTimeline();
    }
  }, [selectedOpportunity, isUrlMode]);

  // ==================================================
  // Handles commercial user selection and 
  // resets opportunity selection
  // ==================================================
  const handleCommercialChange = (newOption) => {
    console.log("handleCommercialChange - newOption :", newOption);
    if (newOption.id) {
      setSelectedCommercial(newOption);
      setSelectedOpportunity(null); // Reset opportunity
      setOpportunityOptions([]);    // Clear existing opportunities
      setStoryData(null); // Reset story data
      setIsLoadingStory(false); // Reset loading state
      setErrorMessage(null); // Reset error message
    }
  };

  // ==================================================
  // Handles opportunity selection and resets 
  // story data
  // ==================================================
  const handleOpportunityChange = (newOpportunity) => {
    console.log("handleOpportunityChange - newOpportunity :", newOpportunity);
    setSelectedOpportunity(newOpportunity);
    setStoryData(null); // Reset story data when opportunity changes
    setIsLoadingStory(false); // Reset loading state
    setErrorMessage(null); // Reset error message
  };

  // ==================================================
  // Filters opportunities based on selected 
  // commercial
  // ==================================================
  const submitFilters = async (user) => {
    try {

      const payload = {
        cur_owner: user?.id || selectedCommercial,
      };

      console.log("submitFilters - user :", user);
      console.log("submitFilters - payload :", payload);

      const response = await apiClient.post(`/api/v1/filter`, payload);

      // Sort opportunities by name
      const sortedOpportunities = response.data.sort((a, b) => a.name.localeCompare(b.name));
      setOpportunityOptions(sortedOpportunities);

      console.log("submitFilters - response.data :", sortedOpportunities);

    } catch (error) {
      console.error("Erreur lors du filtrage des opportunités :", error);
      setOpportunityOptions([]); // Réinitialise si erreur
    }
  };

  // ==================================================
  // Initiates timeline generation and handles 
  // polling for story data
  // ==================================================
  const handleGetTimeline = async () => {
    // Add this check at the beginning of the function
    if (!selectedOpportunity || !selectedOpportunity.id) {
      setErrorMessage("Veuillez sélectionner une opportunité pour générer la timeline.");
      setIsLoadingStory(false); // Ensure loading state is off
      return;
    }

    // Clear existing story data and error message immediately
    setIsLoadingStory(true);
    setStoryData(null);
    setErrorMessage(null); // Clear any previous error messages

    try {
      const requestBody = {
        id: selectedOpportunity.id,
        llm_model: selectedModel,
        max_nb_to_process: maxNbToProcess,
      };

      const url = `/api/v1/story/sumopp`;

      console.log("handleGetTimeline - requestBody:", requestBody);
      console.log("handleGetTimeline - URL with params:", url);

      const response = await apiClient.post(url, requestBody);
      console.log("handleGetTimeline - response:", response);
      
      // Extract job_id from the response
      const job_id_from_response = response.data.body.job_id;
      const story_id_from_response = response.data.body.story_id;
      setJobId(job_id_from_response);
      setStoryId(story_id_from_response);
      console.log("handleGetTimeline - job_id_from_response:", job_id_from_response);
      console.log("handleGetTimeline - story_id_from_response:", story_id_from_response);

      // Automatically send feedback when 'Get Timeline' is hit
      if (selectedOpportunity) {
        const autoFeedbackData = {
          contentId: `opportunity-${selectedOpportunity.value}`,
          storyId: story_id_from_response,
          contentType: "story_viewed",
          contentSection: "story_viewed",
          rating: "positive",
          comment: "",
          timestamp: new Date().toISOString()
        };
        await handleFeedbackSubmit(autoFeedbackData);
      }

      // Polling function
      const pollForStoryData = async (job_id) => {
        const startTime = Date.now();
        const timeout = 5.0 * 60 * 1000; // (min * 60 * 1000)

        while (Date.now() - startTime < timeout) {
          try {
            const pollingResponse = await apiClient.post(
              `/api/v1/polling`,
              { job_id: job_id }
            );

            if (pollingResponse.status === 200 && pollingResponse.data) {
              console.log(
                "handleGetTimeline - Polling response data:",
                pollingResponse.data
              );
              if (pollingResponse.data.status === 'done') {
                console.log("handleGetTimeline - Story data ready!");
                setStoryData(pollingResponse.data.data);
                console.log("handleGetTimeline - data:", pollingResponse.data);
                console.log("handleGetTimeline - story_length:", pollingResponse.data.data.events.length);
                setIsLoadingStory(false);
                setIsWIP(false);
                return; // Exit the polling loop on success
              }
              else if (pollingResponse.data.status === 'failed') {
                console.log("handleGetTimeline - failure...");
                let specificErrorMessage = pollingResponse.data.error_message;
                if (pollingResponse.data.error_message === "No semantic result IDs provided in the event") {
                  specificErrorMessage = "Pas d'événement pertinent trouvé pour cette opportunité";
                }
                setErrorMessage(specificErrorMessage);
                setIsLoadingStory(false);
                setIsWIP(false);
                return;
              }
              else if (pollingResponse.data.status === 'pending') {
                console.log("handleGetTimeline - Story data still pending, continuing to poll...");
                setIsWIP(true); // Set WIP state to true while polling
              }
            }
          } catch (pollingError) {
            if (pollingError.response) {
              if (pollingError.response.status === 404) {
                console.log("handleGetTimeline - Ressorce not found:", pollingError);
                setIsLoadingStory(false);
                return;
              } else if (pollingError.response.status === 500) {
                // Server error, stop polling
                console.error("handleGetTimeline - Server error:", pollingError);
                setIsLoadingStory(false);
                return;
              }
            } else {
              // Network error or other issues
              console.error("handleGetTimeline - Polling error:", pollingError);
              setIsLoadingStory(false);
              return;
            }
          }

          // Wait for 5 seconds before retrying
          await new Promise((resolve) => setTimeout(resolve, 5000));
        }

        console.log("handleGetTimeline - Polling timed out.");
        setIsLoadingStory(false);
      };

      // Start polling
      await pollForStoryData(job_id_from_response); // Add await here to properly handle the promise
    } catch (error) {
      console.error("Error fetching story:", error);
      if (error.response) {
        console.error("Server error details:", error.response.data);
      }
      setStoryData(null);
      setIsLoadingStory(false); // Set loading to false if there's an error
      setJobId(null);
    }
  };

  return (
    <OpportunityFormView

      stage = {STAGE}
    
      commercialOptions={commercialOptions}
      selectedCommercial={selectedCommercial}
      setSelectedCommercial={handleCommercialChange}

      opportunityOptions={opportunityOptions}
      selectedOpportunity={selectedOpportunity}
      setSelectedOpportunity={handleOpportunityChange}

      selectedModel={selectedModel}
      setSelectedModel={setSelectedModel}

      errorMessage={errorMessage}
      storyData={storyData}
      onGetTimeline={handleGetTimeline}
      isLoadingStory={isLoadingStory}
      isWIP={isWIP}
      storyId={storyId}

      isUrlMode={isUrlMode}
      opportunityId={opportunityId}

      maxNbToProcess={maxNbToProcess}
      setMaxNbToProcess={setMaxNbToProcess}
    />
  );
};

export default OpportunityForm;
